import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { environment } from '@src/environments/environment';
import { AppName } from '@src/suite/common/enums/enums';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { ViewStateActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import { selectCurrentApplication } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { selectCurrentViewState } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { getDatabasesOptions, isSearchViewStateRecord, isSingleViewStateRecord } from '@src/suite/common/utils/viewstate-utils';
import { WireApiActions } from '@src/suite/discover/store/actions/discover.actions';
import { LiveSearchService } from '@src/suite/live/services/live-search.service';
import { PlaybookSearchService } from '@src/suite/playbook/services/playbook-search-service';
import { TemplatesSearchService } from '@src/suite/templates/services/templates.search.service';
import { exhaustMap, forkJoin, from, of, switchMap, takeUntil, timer } from 'rxjs';

import { ListActions } from './list.actions';
import { selectListGuids } from './list.selectors';

@Injectable()
export class ListEffects {
    private actions$ = inject(Actions);
    private store = inject(Store);
    private playbookSearchService = inject(PlaybookSearchService);
    private liveSearchService = inject(LiveSearchService);
    private templatesSearchService = inject(TemplatesSearchService);
    private nomWebServicesJson = inject(NomWebServicesJson);
    private globalTablesService = inject(GlobalTablesService);

    // A poll reports the full set matching the view, so it can both add new guids and retire ones that vanished.
    // Reconciling is skipped unless every database answered: a failed one contributes no guids, and pruning
    // against a partial set would retire new items that still exist.
    private buildPollActions(guids: Set<string>, currentListGuids: string[], responsesComplete: boolean): Action[] {
        const actions: Action[] = [];
        const newListGuids = Array.from(guids).filter(guid => !currentListGuids.includes(guid));
        if (newListGuids.length) {
            actions.push(ListActions.addNewListGuids({ listGuids: newListGuids }));
        }
        if (responsesComplete && guids.size) {
            actions.push(ListActions.reconcileNewListGuids({ listGuids: Array.from(guids) }));
        }
        return actions.length ? actions : [ApplicationActions.noOpAction()];
    }

    setCurrentViewStateSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ListActions.startPolling),
            switchMap(() => {
                return timer(0, environment.solr_poll_interval_seconds ? environment.solr_poll_interval_seconds * 1000 : 10000).pipe(
                    takeUntil(
                        this.actions$.pipe(
                            ofType(
                                ViewStateActions.setCurrentViewState,
                                ViewStateActions.viewStateChanged,
                                ViewStateActions.addViewStateFilters,
                                ViewStateActions.removeViewStateFilter,
                                ViewStateActions.updateViewStateFilter,
                                ViewStateActions.adjustDateRange,
                                ApplicationActions.sessionDestroyed
                            )
                        )
                    )
                );
            }),
            concatLatestFrom(() => [this.store.select(selectCurrentApplication), this.store.select(selectCurrentViewState), this.store.select(selectListGuids)]),
            switchMap(([_, application, vs, currentListGuids]) => {
                if (!vs) {
                    return of(WireApiActions.stopPolling({ restart: false, vs: null, interval: null }));
                } else {
                    if (application === AppName.TEMPLATES) {
                        if (isSingleViewStateRecord(vs)) {
                            const databases = getDatabasesOptions(
                                vs,
                                this.nomWebServicesJson.PSTServer,
                                this.nomWebServicesJson.HelloResults.HomeProgram,
                                this.globalTablesService.AllGroups,
                                this.globalTablesService.AllGroupsArray
                            );
                            if (databases.length === 0) {
                                return of(ApplicationActions.noOpAction());
                            }
                            return forkJoin(
                                databases.map(database => {
                                    return from(this.templatesSearchService.getFilteredResultsGuids(vs, database));
                                })
                            ).pipe(
                                exhaustMap(res => {
                                    const guids = new Set<string>();
                                    res.forEach(guidsSet => {
                                        if (guidsSet.status && guidsSet.data?.length) {
                                            guidsSet.data.forEach(guid => {
                                                guids.add(guid.guid);
                                            });
                                        }
                                    });
                                    return of(
                                        ...this.buildPollActions(
                                            guids,
                                            currentListGuids,
                                            res.every(entry => entry.status)
                                        )
                                    );
                                })
                            );
                        } else {
                            return of(ApplicationActions.noOpAction());
                        }
                    }
                    if (application === AppName.WASTEBIN) {
                        const databases = getDatabasesOptions(
                            vs as StorytellingSingleViewStateRecord,
                            this.nomWebServicesJson.PSTServer,
                            this.nomWebServicesJson.PSTGroup,
                            this.globalTablesService.AllGroups,
                            this.globalTablesService.AllGroupsArray
                        );
                        if (databases.length === 0) {
                            return of(ApplicationActions.noOpAction());
                        }
                        return forkJoin(
                            databases.map(database => {
                                return from(this.playbookSearchService.getWastebinResultsGuids(vs, database));
                            })
                        ).pipe(
                            switchMap(response => {
                                const guids = new Set<string>();
                                response.forEach(res => {
                                    if (res.status && res.data?.length) {
                                        res.data.forEach(item => {
                                            if (item.guid) {
                                                guids.add(item.guid);
                                            }
                                        });
                                    }
                                });
                                return of(
                                    ...this.buildPollActions(
                                        guids,
                                        currentListGuids,
                                        response.every(entry => entry.status)
                                    )
                                );
                            })
                        );
                    }
                    if (application === AppName.DISCOVER) {
                        return of(ApplicationActions.noOpAction());
                    }
                    if (application === AppName.LIVE) {
                        if (isSingleViewStateRecord(vs)) {
                            const databases = getDatabasesOptions(
                                vs,
                                this.nomWebServicesJson.PSTServer,
                                this.nomWebServicesJson.HelloResults.HomeProgram,
                                this.globalTablesService.AllGroups,
                                this.globalTablesService.AllGroupsArray
                            );
                            if (databases.length === 0) {
                                return of(ApplicationActions.noOpAction());
                            }
                            return forkJoin(
                                databases.map(database => {
                                    return from(this.liveSearchService.getFilteredResultsGuids(vs, database));
                                })
                            ).pipe(
                                exhaustMap(res => {
                                    const guids = new Set<string>();
                                    res.forEach(guidsSet => {
                                        if (guidsSet.status && guidsSet.data?.length) {
                                            guidsSet.data.forEach(guid => {
                                                guids.add(guid.guid);
                                            });
                                        }
                                    });
                                    return of(
                                        ...this.buildPollActions(
                                            guids,
                                            currentListGuids,
                                            res.every(entry => entry.status)
                                        )
                                    );
                                })
                            );
                        } else {
                            return of(ApplicationActions.noOpAction());
                        }
                    }
                    if (application === AppName.PLAYBOOK) {
                        const databases = getDatabasesOptions(
                            vs as StorytellingSingleViewStateRecord,
                            this.nomWebServicesJson.PSTServer,
                            this.nomWebServicesJson.PSTGroup,
                            this.globalTablesService.AllGroups,
                            this.globalTablesService.AllGroupsArray
                        );
                        if (databases.length === 0) {
                            return of(ApplicationActions.noOpAction());
                        }
                        return forkJoin(
                            databases
                                .map(database => {
                                    const requests = [from(this.playbookSearchService.getFilteredResultsGuids(vs, null, database))];
                                    // Rundowns only participate in SEARCH view states (loadPlaybookObjects$
                                    // skips loadRundowns otherwise), so only search views need the second
                                    // SearchFields query against the rundown index.
                                    if (isSearchViewStateRecord(vs)) {
                                        requests.push(from(this.liveSearchService.getFilteredResultsGuids(vs, database)));
                                    }
                                    return requests;
                                })
                                .flat()
                        ).pipe(
                            switchMap(response => {
                                const guids = new Set<string>();
                                response.forEach(res => {
                                    if (res.status && res.data?.length) {
                                        res.data.forEach(item => {
                                            if (item.guid) {
                                                guids.add(item.guid);
                                            }
                                        });
                                    }
                                });
                                return of(
                                    ...this.buildPollActions(
                                        guids,
                                        currentListGuids,
                                        response.every(entry => entry.status)
                                    )
                                );
                            })
                        );
                    }
                    return of(ApplicationActions.noOpAction());
                }
            })
        );
    });
}
